import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { reportsAPI } from '../../services/api';
import ReportDisplay from './ReportDisplay';
import useModalOverlayClose from '../../hooks/useModalOverlayClose';
import { rowsToDisplay } from '../../utils/cubeQuery';
import { downloadCsv } from '../../utils/reportExport';

// Rows are listed this many at a time; a download takes up to DOWNLOAD_LIMIT.
export const PAGE_SIZE = 200;
export const DOWNLOAD_LIMIT = 10000;
// A dataset's columns shown, at most.
const MAX_COLUMNS = 40;

const cubeOf = (member) => String(member || '').split('.')[0];

/** Expand a day-start value to the whole bucket for inDateRange filters. */
function widen(filter, bucket) {
  const start = new Date(filter.values[0]);
  const end = new Date(start);
  switch (bucket) {
    case 'week': end.setDate(end.getDate() + 6); break;
    case 'month': end.setMonth(end.getMonth() + 1); end.setDate(0); break;
    case 'quarter': end.setMonth(end.getMonth() + 3); end.setDate(0); break;
    case 'year': end.setFullYear(end.getFullYear() + 1); end.setDate(0); break;
    default: break;
  }
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { member: filter.member, operator: 'inDateRange', values: [iso(start), iso(end)] };
}

/** The filters a clicked row adds: each of its categories, and a date bucket as its whole period. */
function filtersFromRow(row, annotation) {
  const keys = { ...(annotation.dimensions || {}), ...(annotation.timeDimensions || {}) };
  const out = [];
  for (const [key, meta] of Object.entries(keys)) {
    const label = meta.title || meta.shortTitle || key;
    const value = row?.[label] ?? row?.[key];
    if (value === undefined || value === null || value === '') continue;
    const bucket = /\.(day|week|month|quarter|year)$/.exec(key)?.[1];
    const member = key.replace(/\.(day|week|month|quarter|year)$/, '');
    out.push(bucket
      ? { member, operator: 'inDateRange', values: [String(value).slice(0, 10), String(value).slice(0, 10)], bucket, shown: String(value).slice(0, 10) }
      : { member, operator: 'equals', values: [String(value)], shown: String(value) });
  }
  return out;
}

/**
 * Drill-down: the records behind a figure in a report. The rows are those
 * the figure counts: the report's period and filters, the clicked category,
 * and the figure's own conditions (Cube's measureFilter applies them, so an
 * "Overdue" figure lists only unpaid invoices past their due date). With
 * several figures, the one to show is chosen. The dataset's columns are
 * listed with the report's own first; rows come a page at a time, and the
 * download takes them all, up to DOWNLOAD_LIMIT.
 *
 * measure: the figure clicked, when known (a bar's series, a stat's value).
 */
const NONE = {};

const DrillModal = ({ companyId, row, annotation = NONE, query: given, title, measure = null, onClose }) => {
  const overlay = useModalOverlayClose(onClose);
  // Keyed on content, so a caller building these on each render does not fetch again.
  const queryKey = JSON.stringify(given);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const query = useMemo(() => given, [queryKey]);
  const clickKey = JSON.stringify([row, annotation]);
  const figures = useMemo(() => (query.measures || []).filter((m) => !String(m).startsWith('calc:')), [query.measures]);
  const [figure, setFigure] = useState(() => (measure && figures.includes(measure) ? measure : figures[0] || null));
  const [meta, setMeta] = useState(null);
  const [state, setState] = useState({ loading: true, error: null, rows: [], more: false });
  const [downloading, setDownloading] = useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const clicked = useMemo(() => filtersFromRow(row, annotation), [clickKey]);

  useEffect(() => {
    let cancelled = false;
    reportsAPI.getMeta(companyId).then((res) => { if (!cancelled) setMeta(res?.data || { cubes: [] }); })
      .catch((err) => { if (!cancelled) setState({ loading: false, error: err.response?.data?.error || err.message, rows: [], more: false }); });
    return () => { cancelled = true; };
  }, [companyId]);

  const members = useMemo(() => new Map((meta?.cubes || []).flatMap((c) => [...(c.dimensions || []), ...(c.measures || [])]).map((m) => [m.name, m])), [meta]);
  const label = (name) => members.get(name)?.shortTitle || members.get(name)?.title || String(name).split('.').pop().replace(/_/g, ' ');

  // The query for the rows: everything but the page.
  const base = useMemo(() => {
    if (!meta) return null;
    const cubeName = cubeOf(figure || query.dimensions?.[0] || query.timeDimensions?.[0]?.dimension);
    const cube = (meta.cubes || []).find((c) => c.name === cubeName);
    const all = (cube?.dimensions || []).filter((d) => d.public !== false && d.isVisible !== false).map((d) => d.name);
    // The report's own columns first, then the rest of the dataset's.
    const own = [...(query.dimensions || []), ...(query.timeDimensions || []).map((t) => t.dimension)].filter((d) => all.includes(d));
    const dimensions = [...new Set([...own, ...all])].slice(0, MAX_COLUMNS);
    // The report's period and filters still apply: the rows behind a bar for this week are this week's.
    const periods = (query.timeDimensions || []).filter((t) => t.dimension && t.dateRange).map((t) => ({ dimension: t.dimension, dateRange: t.dateRange }));
    return {
      dimensions,
      timeDimensions: periods,
      filters: [
        ...(query.filters || []),
        ...clicked.map(({ bucket, shown: _shown, ...f }) => (f.operator === 'inDateRange' ? widen(f, bucket) : f)),
        // Only the rows the figure counts, by its own conditions.
        ...(figure ? [{ member: figure, operator: 'measureFilter' }] : []),
      ],
      ...(periods[0] && dimensions.includes(periods[0].dimension) ? { order: { [periods[0].dimension]: 'desc' } } : {}),
    };
  }, [meta, figure, query, clicked]);

  const load = async (offset) => {
    const res = await reportsAPI.query(companyId, { ...base, limit: PAGE_SIZE, ...(offset ? { offset } : {}) });
    if (!res?.success) throw new Error(res?.error || 'Drill-down failed');
    return { rows: rowsToDisplay(res.data.data, res.data.annotation, { shortTitles: true }), count: (res.data.data || []).length };
  };

  useEffect(() => {
    if (!base) return undefined;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    load(0)
      .then(({ rows, count }) => { if (!cancelled) setState({ loading: false, error: null, rows, more: count === PAGE_SIZE }); })
      .catch((err) => { if (!cancelled) setState({ loading: false, error: err.response?.data?.error || err.message, rows: [], more: false }); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  const showMore = async () => {
    setState((s) => ({ ...s, loadingMore: true }));
    try {
      const { rows, count } = await load(state.rows.length);
      setState((s) => ({ ...s, rows: [...s.rows, ...rows], more: count === PAGE_SIZE, loadingMore: false }));
    } catch (err) {
      setState((s) => ({ ...s, error: err.response?.data?.error || err.message, loadingMore: false }));
    }
  };

  const download = async () => {
    setDownloading(true);
    try {
      const res = await reportsAPI.query(companyId, { ...base, limit: DOWNLOAD_LIMIT });
      if (!res?.success) throw new Error(res?.error || 'Download failed');
      downloadCsv(rowsToDisplay(res.data.data, res.data.annotation, { shortTitles: true }), `${title} rows`);
    } catch (err) {
      setState((s) => ({ ...s, error: err.response?.data?.error || err.message }));
    } finally {
      setDownloading(false);
    }
  };

  const count = state.rows.length;
  return createPortal(
    <div className="modal-overlay" {...overlay}>
      <div className="modal-content drill-modal drill-modal--full" role="dialog" aria-modal="true" aria-label={`${title}: rows behind this`} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}: rows behind this</h2>
          <button onClick={onClose} className="modal-close" aria-label="Close">&times;</button>
        </div>
        <div className="drill-filters">
          {figures.length > 1 ? (
            <label className="drill-figure">
              <span>Rows counted in</span>
              <select value={figure || ''} onChange={(e) => setFigure(e.target.value)} aria-label="Rows counted in">
                {figures.map((m) => <option key={m} value={m}>{label(m)}</option>)}
              </select>
            </label>
          ) : figure && <span className="selection-chip">Rows counted in {label(figure)}</span>}
          {clicked.map((f) => <span key={f.member} className="selection-chip">{label(f.member)}: {f.shown}</span>)}
        </div>
        <div className="drill-body widget report-widget">
          <div className="widget-body report-body">
            {state.loading ? <div className="report-status">Loading…</div>
              : state.error ? <div className="report-status report-status--error">⚠ {state.error}</div>
                : <ReportDisplay rows={state.rows} config={{ displayType: 'table' }} />}
          </div>
        </div>
        <div className="form-actions">
          <span className="dataset-empty">{state.more ? `First ${count.toLocaleString()} rows` : `${count.toLocaleString()} row${count === 1 ? '' : 's'}`}</span>
          {state.more && <button className="btn-secondary" disabled={state.loadingMore} onClick={showMore}>{state.loadingMore ? 'Loading…' : `Show ${PAGE_SIZE} more`}</button>}
          <button className="btn-secondary" disabled={!count || downloading} onClick={download}
            title={`Every row, up to ${DOWNLOAD_LIMIT.toLocaleString()}`}>{downloading ? 'Preparing…' : 'Download CSV'}</button>
          <button className="btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DrillModal;
